/*
 * Copyright 2022 Benjamin Menant.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    EmptyState, EmptyStateActions,
    EmptyStateBody, EmptyStateFooter,
    EmptyStateIcon,
    PageSection,
    PageSectionVariants,
    Spinner,
    Text,
    TextContent,
    Title,
} from '@patternfly/react-core';
import {Page, usePromise} from "@keycloak/keycloak-account-ui";
import { UnknownIcon } from '@patternfly/react-icons';
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {CardRecurringCommitment} from './CardRecurringCommitment';
import {DirectDebitMandate} from './DirectDebitMandate';
import {IAMRecurringCommitmentJsonSchema as RC} from '../../donor-service/types/IAMRecurringCommitmentJsonSchema';
import {IAMDirectDebitMandateJsonSchema as DDM} from '../../donor-service/types/IAMDirectDebitMandateJsonSchema';
import {useEnvironmentWithDonorService} from "../../context.tsx";

type DonorRecurringCommitments = [string, RC | DDM][];

export function RecurringCommitmentsPage () {
    const { t } = useTranslation();

    const context = useEnvironmentWithDonorService();

    const [commitments, setCommitments] = useState<DonorRecurringCommitments>();

    usePromise(
        async () => {
            const { recurringCommitments } = await context.donorService.fetchDonorRecord();
            return Object.entries(recurringCommitments);
        },
        setCommitments,
    );

    if (!commitments) {
        return <Spinner />;
    }

    if (commitments.length === 0) {
        return (
            <EmptyState variant="xl">
                <EmptyStateIcon icon={UnknownIcon} />
                <Title headingLevel="h2" size="2xl">
                    {t("noRecurringCommitments")}
                </Title>
                <EmptyStateBody>
                    {t("missingCards")}
                </EmptyStateBody>
                <EmptyStateFooter>
                    <EmptyStateActions>
                        <strong>{t("reportMissingCommitments")}</strong>
                    </EmptyStateActions>
                </EmptyStateFooter>
            </EmptyState>
        );
    }

    const listOfCommitments = commitments.map(([refId, data]) => {
        return isMandate(data)
            ? (<DirectDebitMandate key={refId} mandate={data} />)
            : (<CardRecurringCommitment key={refId} commitment={data} />);
    });

    return (
        <Page title={t("recurringCommitments")} description={t("recurringCommitmentsIntro")}>
            <PageSection variant={PageSectionVariants.light} padding={{ default: 'noPadding' }}>
                <TextContent>
                    <Text>
                        <strong>{t("notice")}&nbsp;</strong>
                        {t("recurringCommitmentsNoCard")}&nbsp;
                        <Text component="a" href={t('contactPageHref')} style={{textTransform: 'lowercase'}} target="_blank" rel="noreferrer noopener">{t("doContactUs")}</Text>.
                    </Text>
                </TextContent>
            </PageSection>

            <PageSection variant={PageSectionVariants.light}  padding={{ default: 'padding' }}>
                {
                    commitments.length > 0
                        ? listOfCommitments
                        : (
                            <EmptyState variant="xl">
                                <EmptyStateIcon icon={UnknownIcon} />
                                <Title headingLevel="h2" size="2xl">
                                    {t("noRecurringCommitments")}
                                </Title>
                                <EmptyStateBody>
                                    {t("missingCards")}
                                </EmptyStateBody>
                                <EmptyStateFooter>
                                    <EmptyStateActions>
                                        <strong>{t("reportMissingCommitments")}</strong>
                                    </EmptyStateActions>
                                </EmptyStateFooter>
                            </EmptyState>
                        )
                }
            </PageSection>
        </Page>
    );
}

function isMandate(commitment: RC): commitment is DDM {
    return commitment.mode === 'direct-debit';
}
